<template>
  <div>
    <div class="row" ref="editor">
      <template v-if="editing">
        <div class="col-3">
          <DaySelect :history="true" :today="true" :future="false"  @configChanged="config = $event" :currentConfig="config"/>
        </div>
        <div class="col-3">
          <ActivitySelect  @configChanged="config = $event" :currentConfig="config"/>
        </div>
        <div class="col-3">
          <IncludeWeekends @configChanged="config = $event" :currentConfig="config"></IncludeWeekends>
        </div>
        <div class="col-3">
          <label for="title-planning">{{ $t('ui.title') }}</label>
          <input type="text" id="title-planning" class="form-control" v-model="config.title">
        </div>
        <div class="col-3">
          <NormPerDayTime @configChanged="config = $event" :currentConfig="config"></NormPerDayTime>
        </div>
        <div class="col-3">
          <NormPerDayAmount @configChanged="config = $event" :currentConfig="config"></NormPerDayAmount>
        </div>
        <div class="col-6 align-right align-vertical-bottom">
          <AmountViewToggle @configChanged="config = $event" :currentConfig="config"></AmountViewToggle>
        </div>
      </template>
    </div>
    <div class="row" v-if="!editing">
      <div class="col-12 align-right">
        <AmountViewToggle @configChanged="config = $event" :currentConfig="config"></AmountViewToggle>
      </div>
    </div>
    <div class="chart-container" v-if="!editing">
      <component-load-spinner :loading="loading"></component-load-spinner>
      <bar-chart v-if="getAnnotations()"
                 style="width: 100%; height: 100%"
                 :style="loading ? 'opacity:0.5' : 'opacity:1'"
                 :class="loading ? 'loading':''"
                 :chartData="getChartData"
                 :options="{ ...getOptions, ...getAnnotations() }"/>
    </div>
  </div>


</template>

<script>
import {mapGetters} from "vuex";

import DaySelect from "@/components/insights/forms/DaySelect";
import BarChart from '@/components/insights/charts/barChart.js'

import timeCalc from "@/mixins/timeCalc";
import generalMethods from "@/mixins/insights/generalMethods";
import chartColors from "@/mixins/insights/chartColors";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import ActivitySelect from "@/components/insights/forms/ActivitySelect";
import IncludeWeekends from "@/components/insights/forms/IncludeWeekends";
import NormPerDayTime from "@/components/insights/forms/NormPerDayTime";
import NormPerDayAmount from "@/components/insights/forms/NormPerDayAmount";
import AmountViewToggle from "@/components/insights/forms/AmountViewToggle";

import timespan from "@/mixins/timespan";
import ComponentLoadSpinner from "@/components/general/ComponentLoadSpinner";

export default {
  components: {
    ComponentLoadSpinner,
    IncludeWeekends,
    ActivitySelect,
    DaySelect,
    BarChart,
    AmountViewToggle,
    NormPerDayTime,
    NormPerDayAmount
  },
  mixins: [timeCalc, generalMethods, chartColors, timespan],
  props: ['editing'],
  data() {
    return {
      plannedJobs: [],
      finishedJobs: [],
      loading: true,
      priorJobs: [],
      config: {
        dayDifference: {start: 0, end: 0},
        activity: null,
        includeWeekends: false,
        includeDutchHolidays: false,
        amountView: true, // true = amount, false = time
        normPerDayInTime: {
          unit: 'H',
          value: 0
        },
        normPerDayAmount: 0
      }
    }
  },
  computed: {
    ...mapGetters('users', ['getClaims']),
    getPlannedJobs() {
      return this.plannedJobs
    },
    getFinishedJobs() {
      return this.finishedJobs
    },
    getOptions() {
      const self = this

      let viewSpecificOptions = {
        yAxes: {
          ticks: {}
        },
        tooltips: {
          callbacks: {}
        }
      };
      if (this.config.amountView) {
        viewSpecificOptions.tooltips.callbacks.title = function (tooltip) {
          return tooltip[0].label
        };
        viewSpecificOptions.tooltips.callbacks.label = function (tooltipItem) {
          return tooltipItem.value
        };
      } else {
        viewSpecificOptions.yAxes.ticks.stepSize = ((1000 * 60 * 60)) * 5;
        viewSpecificOptions.yAxes.ticks.callback = function (label) {
          return self.getHoursFromMilliseconds(label, false) + ' hour(s)'
        };
        viewSpecificOptions.tooltips.callbacks.title = function (tooltip) {
          return moment(tooltip[0].label, 'DD-MM-YYYY').format('DD MMM')
        };
        viewSpecificOptions.tooltips.callbacks.label = function (tooltipItem) {
          return self.formattedColonTime(tooltipItem.value) + ' hour(s)'   //tooltipItem.value
        };
      }
      return {
        ...this.generalChartOptions,
        animation: {
          onComplete: () => {

            self.loading = false;
          }
        },
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false
              },
              // bounds: 'ticks',
              offset: true, // IF this is set to false, it will not display the last bar!
              stacked: true,
              autoSkip: false,
              startAt: 0,
            },
          ],
          yAxes: [{
            ticks: {
              beginAtZero: true,
              autoSkip: true,
              type: 'linear',
              ...viewSpecificOptions.yAxes.ticks
            },
          }],
        },
        tooltips: {
          ...viewSpecificOptions.tooltips
        },
        plugins: {
          datalabels: {
            color: 'black',
            anchor: 'start',
            align: 'bottom',
            font: {
              // weight: 'bold',
              size: 20
            },
            offset: -50,

            display: function (ctx) {
              return false
              return ctx.datasetIndex > 0 //ctx.chart.$totalizer.utmost;
            },
            formatter: (value, ctx) => {
              return self.getHoursFromMilliseconds(value.y, false) > 0 ? self.getHoursFromMilliseconds(value.y, false) + '' : ' '
            },
          }
        },
        responsive: true,
        maintainAspectRatio: false,
      }
    },
    getLabels() {
      const dates = this.getDatesOfChart();
      return _.map(dates, date => {
        return moment(date).format('DD-MM-YYYY')
      });
    },
    getChartData() {
      return {
        labels: this.getLabels,
        datasets: [
          {
            stack: 'Stack done',
            label: this.$t('insights.producedPerActivity.finishedJob'),
            backgroundColor: this.colors.producedPerActivity.finishedJob,
            data: this.getChartDataFinishedPerDate,
          }
        ]
      }
    },
    getFinishedJobsGroupedByFinishedDate() {
      return _.groupBy(this.getFinishedJobs, job => {
        return moment(job.finishedDate.toDate()).startOf('day').format('YYYY-MM-DD')
      })
    },
    getChartDataFinishedPerDate() {
      let returnValues = [];
      if (this.config.amountView === true) {
        const dates = this.getDatesOfChart();
        dates.forEach(date => {
          const jobsOfDate = this.getFinishedJobsGroupedByFinishedDate[moment(date).format('YYYY-MM-DD')];
          if (jobsOfDate) {
            const sum = _.sumBy(jobsOfDate, 'amount');
            returnValues.push(sum ? sum : 0);
          } else {
            returnValues.push(0);
          }
        });
      } else {
        const dates = this.getDatesOfChart();
        dates.forEach(date => {
          const jobsOfDate = this.getFinishedJobsGroupedByFinishedDate[moment(date).format('YYYY-MM-DD')];
          if (jobsOfDate) {
            const sum = _.sumBy(jobsOfDate, 'timeSpent');
            returnValues.push(sum ? sum : 0);
          } else {
            returnValues.push(0);
          }
        });
      }
      return returnValues;
    },
    getAnnotationValue() {
      if (this.config.amountView) {
        if (this.config.normPerDayAmount === null || this.config.normPerDayAmount === undefined) {
          return null;
        } else {
          return this.config.normPerDayAmount;
        }
      } else {
        if (!this.config.normPerDayInTime || this.config.normPerDayInTime.value === null || this.config.normPerDayInTime.value === undefined) {
          return null;
        }
        const value = this.config.normPerDayInTime.value;
        const unit = this.config.normPerDayInTime.unit;

        if (unit === 'H') {
          return value * 3600000;
        } else if (unit === 'M') {
          return value * 60000;
        }
        return 0;
      }
    }
  },
  methods: {
    getAnnotations() {
      return {
        annotation: {
          annotations: [
            {
              borderColor: this.colors.producedPerActivity.annotationColor,
              borderWidth: 2,
              mode: 'horizontal',
              type: 'line',
              value: this.getAnnotationValue,
              scaleID: 'y-axis-0'
            }
          ]
        }
      }
    },
    async getPriorJob(documentId) {
      const priorJobSnapshot = await this.$FBClient.firestore().collection('jobs').doc(documentId).get();
      if (priorJobSnapshot.exists) {
        return priorJobSnapshot.data();
      } else {
        return null;
      }
    },
    getDatesOfChart() {
      if (!this.config.dayDifference) {
        return []
      }
      return this.getDatesWithinRangeFromToday(this.config.dayDifference.start, this.config.dayDifference.end - this.config.dayDifference.start, this.config.includeWeekends, this.config.includeDutchHolidays);
    },
    async getJobsFromFilter() {
      this.loading = true;

      let userClaims = this.getClaims;
      const dates = this.getDatesOfChart();
      let startDay = moment(_.first(dates)).startOf('day');
      let endDay = moment(_.last(dates)).endOf('day');
      try {
        let finishedJobs = (await this.$FBClient.firestore().collection('jobs')
            .where('companyId', '==', userClaims.companyId)
            .where('finishedDate', '>=', startDay.toDate())
            .where('finishedDate', '<=', endDay.toDate())
            .where('activity', '==', this.config.activity)
            .get()).docs.map(doc => {
          return doc.data()
        });


        this.finishedJobs = finishedJobs;

        this.loading = false;
      } catch (e) {
        console.warn(e);
      }
      return false
    }

  },
  watch: {
    'config.dayDifference': {
      handler() {
        this.getJobsFromFilter()
      },
      deep: true
    },
    'config.activity'() {
      this.getJobsFromFilter();
    },
    'config.includeWeekends'() {
      this.getJobsFromFilter()
    },
    'config.includeDutchHolidays'() {
      this.getJobsFromFilter()
    }
  }
}
</script>

<style scoped lang="scss">
@import "./src/assets/scss/components/insights/_generalStylingCharts.scss";
.align-right {
  text-align: right;
}

.align-vertical-bottom {
  align-self: end;
}


</style>